import { useState, useEffect } from "react"
import FormSection from "../../components/ui/FormSection"
import FormInput from "../../components/layout/FormInput"
import Button from "../../components/ui/Button"
import DashboardHeader from "../../components/dashboard/HeaderDashboard"
import UserController from "@/network/controllers/user.controller"
import { useSession } from "@/hooks/useSession"
import {STATE, CurrentEntityState} from "@state"

export default function InvestigatorSettings() {
  const [activeTab, setActiveTab] = useState("Investigator information")
  const [loadingUserInfo, setLoadingUserInfo] = useState(true)
  const [errorUserInfo, setErrorUserInfo] = useState(null)
  const { user } = useSession()
  const currentUserId = user?.userId 

  // Investigator information state
  const [investigatorInfo, setInvestigatorInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    street: "",
    number: "",
    country: "",
    city: "",
    postalCode: "",
  })

   useEffect(() => {
    const fetchUserInfo = async () => {
      if (!currentUserId) {
        setErrorUserInfo("User id is not available to fetch user information")
        setLoadingUserInfo(false)
        return
      }
      setLoadingUserInfo(true)
      setErrorUserInfo(null)
      try{
        const response = await UserController.getUserInfo(currentUserId)
        console.log("Response complete de l Api (information utilisateur :", response)
        if (response?.success !== false) {
          const transformedData = {
            firstName: response.firstname || "",
            lastName: response.lastname || "",
            email: response.email || "",
            contactNumber: response.phone_number || "",
            street: (response.postal_address_1 || "").split(", ")[0] || "",
            number: (response.postal_address_1 || "").split(", ")[1] || "",
            country: response.country || "",
            city: response.city || "",
            postalCode: response.zipcode || "",
          }
          setInvestigatorInfo(transformedData)
          console.log("Données utilisateur transformées et définies dans l'état userInfo :", transformedData)
        } else {
          setErrorUserInfo(response.message || "Échec de la récupération des informations utilisateur.")
        }
      } catch (err) {
        console.error("Erreur réseau ou inattendue lors de la récupération des informations utilisateur :", err)
        setErrorUserInfo("Une erreur inattendue est survenue lors de la récupération des informations utilisateur.")
      } finally {
        setLoadingUserInfo(false)
      }
    }
    if(currentUserId) {
      fetchUserInfo()
    } else {
      setLoadingUserInfo(false)
      setErrorUserInfo("User ID not available to fetch user information.")
    }
  }, [currentUserId])

  // Security state
  const [security, setSecurity] = useState({
    password: "",
    confirmPassword: "",
  })

  // Password validation state
  const [passwordError, setPasswordError] = useState("")

  // Handle investigator info input change
  const handleInvestigatorInfoChange = (e) => {
    const { name, value } = e.target
    setInvestigatorInfo((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Handle security input change
  const handleSecurityChange = (e) => {
    const { name, value } = e.target
    setSecurity((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Clear password error when typing
    if (passwordError) {
      setPasswordError("")
    }
  }
   // -----------------------------------------------
   // TODO: Call blockchain to update the investigator information 
   // -----------------------------------------------
   
  // Update investigator information
  const handleUpdateInvestigatorInfo = async () => {
    if (!currentUserId) {
      alert("User ID missing.");
      return;
    }
  
    const addressStr = [investigatorInfo.street, investigatorInfo.number]
      .filter(Boolean)
      .join(", ");
  
    const payload = {
      firstname: (investigatorInfo.firstName || "").trim() || null,
      lastname: (investigatorInfo.lastName || "").trim() || null,
      email: (investigatorInfo.email || "").trim() || null,
      phone_number: (investigatorInfo.contactNumber || "").trim() || null,
      postal_address_1: addressStr || null,
      postal_address_2: addressStr || null,
      country: (investigatorInfo.country || "").trim() || null,
      city: (investigatorInfo.city || "").trim() || null,
      zipcode: (investigatorInfo.postalCode || "").trim() || null,
    };
  
    try {
      const res = await UserController.updateUserInfo(currentUserId, payload);
  
      if (res?.success === false) {
        alert(res.message || "Update failed.");
        return;
      }
  
      const u = res?.data ?? payload;
  
      const updated = {
        firstName: u.firstname ?? investigatorInfo.firstName,
        lastName: u.lastname ?? investigatorInfo.lastName,
        email: u.email ?? investigatorInfo.email,
        contactNumber: u.phone_number ?? investigatorInfo.contactNumber,
        street: (u.postal_address_1 ?? addressStr ?? "").split(", ")[0] || "",
        number: (u.postal_address_1 ?? addressStr ?? "").split(", ")[1] || "",
        country: u.country ?? investigatorInfo.country,
        city: u.city ?? investigatorInfo.city,
        postalCode: u.zipcode ?? investigatorInfo.postalCode,
      };
  
      setInvestigatorInfo(updated);
      alert("Investigator information updated successfully!");
    } catch (e) {
      console.error("Error updating investigator info:", e);
      alert("Unexpected error during update.");
    }
  };

  // Update password
  const handleUpdatePassword = () => {
    // Validate passwords
    if (security.password.length < 8) {
      setPasswordError("Password must be at least 8 characters long")
      return
    }

    if (security.password !== security.confirmPassword) {
      setPasswordError("Passwords do not match")
      return
    }


    console.log("Updating password")

    // Reset form
    setSecurity({
      password: "",
      confirmPassword: "",
    })

    // Show success message
    alert("Password updated successfully!")
  }

  // Define the buttons array for the tabs
  const tabButtons = ["Investigator information", /* "Security" */]

  return (
    <div className="flex-1 flex flex-col overflow-hidden p-4 md:p-6 lg:p-8">
      <DashboardHeader
        title="Settings"
        buttons={tabButtons}
        activeButton={activeTab}
        onButtonClick={(tab) => setActiveTab(tab)}
      />
      <div className="flex-1 overflow-auto bg-dashboard-bg">
      <div className="flex-1 mt-2 rounded-xl  bg-dashboard-bg1">
          {/* Investigator Information Tab */}
          {activeTab === "Investigator information" && (
            <main className="p-3 sm:p-4 md:p-6">
              <FormSection title="Investigator information">
                <div className="flex flex-col md:flex-row">
                  {/* Form inputs - 2/3 width */}
                  <div className="md:w-2/3 pr-6">
                    {/* Nom et Prenom  */}
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <div className="flex-1">
                        <FormInput
                          label="First name"
                          name="firstName"
                          value={investigatorInfo.firstName}
                          onChange={handleInvestigatorInfoChange}
                        />
                      </div>
                      <div className="flex-1">
                        <FormInput
                          label="Last name"
                          name="lastName"
                          value={investigatorInfo.lastName}
                          onChange={handleInvestigatorInfoChange}
                        />
                      </div>
                    </div>

                    {/*Email et telephone  */}
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <div className="flex-1">
                        <FormInput
                          label="Email address"
                          name="email"
                          value={investigatorInfo.email}
                          onChange={handleInvestigatorInfoChange}
                        />
                      </div>
                      <div className="flex-1">
                        <FormInput
                          label="Contact number"
                          name="contactNumber"
                          value={investigatorInfo.contactNumber}
                          onChange={handleInvestigatorInfoChange}
                        />
                      </div>
                    </div>

                    {/* addresse et numero  */}
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <div className="flex-grow">
                        <FormInput
                          label="Office address - Street"
                          name="street"
                          value={investigatorInfo.street}
                          onChange={handleInvestigatorInfoChange}
                        />
                      </div>
                      <div className="md:w-1/4">
                        <FormInput
                          label="Number"
                          name="number"
                          value={investigatorInfo.number}
                          onChange={handleInvestigatorInfoChange}
                        />
                      </div>
                    </div>

                    {/* Pays, ville , code postal */}
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <div className="flex-1">
                        <FormInput
                          label="Country"
                          name="country"
                          value={investigatorInfo.country}
                          onChange={handleInvestigatorInfoChange}
                        />
                      </div>
                      <div className="flex-1">
                        <FormInput
                          label="City / Town"
                          name="city"
                          value={investigatorInfo.city}
                          onChange={handleInvestigatorInfoChange}
                        />
                      </div>
                      <div className="flex-1">
                        <FormInput
                          label="Postal code"
                          name="postalCode"
                          value={investigatorInfo.postalCode}
                          onChange={handleInvestigatorInfoChange}
                        />
                      </div>
                    </div>

                    <div className="p-6 flex justify-center">
                      <Button
                        onClick={handleUpdateInvestigatorInfo}
                        variant="addbutton"
                        className="w-[50%] text-white font-bold"
                      >
                        Update information
                      </Button>
                    </div>
                    <div className="h-20"></div>
                  </div>

                  {/* Logo section - 1/3 width */}
                  <div className="md:w-1/3 flex flex-col items-center">
                    <div className="w-82 h-82 mt-4">
                    <img src="/img/logo2.png" alt="Oshimed Logo" className="w-full h-full object-contain" />
                      
                    </div>

                    {/* Logo text */}
                   {/*  <div className="mt-4 text-center">
                      <h3 className="text-linkColor text-4xl font-bold">OSHIMED</h3>
                      <p className="text-linkColor text-lg mt-2">Your consent, your rules</p>
                    </div> */}
                  </div>
                </div>
              </FormSection>
            </main>
          )}

          {/* Security Tab */}
          {activeTab === "Security" && (
           <main className="p-3 sm:p-4 md:p-6">
              <FormSection title="Security">
                <div className="flex flex-col md:flex-row">
                  {/* Form inputs - 1/3 width */}
                  <div className="md:w-2/3 pr-6">
                    <h3 className="text-lg font-semibold mb-2">Set a new password</h3>
                    <p className="text-gray-600 mb-4">Your password secure your access to your account.</p>
                    <p className="text-gray-600 mb-6">
                      If you loose your password, you can't retrieve it via your email address.
                    </p>

                    <div className="mb-4 w-[50%]">
                      <FormInput
                        label="Password"
                        name="password"
                        type="password"
                        value={security.password}
                        onChange={handleSecurityChange}
                        placeholder="At least 8 characters"
                      />
                    </div>

                    <div className="mb-4 w-[50%]">
                      <FormInput
                        label="Re-enter password"
                        name="confirmPassword"
                        type="password"
                        value={security.confirmPassword}
                        onChange={handleSecurityChange}
                        placeholder="At least 8 characters"
                      />
                    </div>

                    {/* Password Error */}
                    {passwordError && <div className="mt-2 text-red-500">{passwordError}</div>}

                    <div className="mt-6">
                      <Button
                        onClick={handleUpdatePassword}
                        variant="addbutton"
                        className="w-[50%] text-white font-bold"
                      >
                        Update password
                      </Button>
                      </div>
                    <div className="h-20"></div>
                    <div className="h-10"></div>
                  </div>

                  {/* Logo section - 1/3 width */}
                  <div className="md:w-1/3 flex flex-col items-center">
                    <div className="w-82 h-82 mt-4">
                      <img src="/img/logo2.png" alt="Oshimed Logo" className="w-full h-full object-contain" />
                    </div>
              
                  </div>
                </div>
              </FormSection>
            </main>
          )}
        </div>
      </div>
    </div>
  )
}
